import React, { memo } from 'react';
import { Row, Col, Card, Image } from 'react-bootstrap';
import PageWrapper from '../../components/PageWrapper';
import {
  FaCheckCircle,
  FaUserMd,
  FaFlask,
  FaProcedures,
  FaNotesMedical,
  FaHeartbeat,
  FaIdCard,
} from 'react-icons/fa';

/* =======================
   CONSTANTS (OUTSIDE)
======================= */

const ICON_MAP = {
  FaCheckCircle,
  FaUserMd,
  FaFlask,
  FaProcedures,
  FaNotesMedical,
  FaHeartbeat,
  FaIdCard,
};
const OPD_ICONS = [FaUserMd, FaFlask, FaProcedures];

/* =======================
   COMPONENT
======================= */

const PageStructure2 = memo(({ data }) => {
  return (
    <PageWrapper title={data.maintitle} subtitle={data.mainsubtitle}>
      <div className="px-3">
        {/* PAGE HEADER */}
        <div className="text-center my-4">
          <h1 className="fw-bold">{data.title}</h1>
          <p className="mt-2 primary-class">{data.subtitle}</p>
        </div>

        {/* BANNER */}
        {data.banner && (
          <div className="d-flex justify-content-center mb-5 px-3">
            <Image
              src={data.banner}
              className="w-100 img-fluid shadow-sm"
              style={{
                width: '100%',
                maxWidth: '1200px',
                height: 'auto',
                borderRadius: '12px',
              }}
            />
          </div>
        )}

        {/* OPD SECTION */}
        <div className="text-center my-5">
          <h3 className="fw-bold">OPD Medicine & Service Advice</h3>
        </div>
        <Row className="g-4 pb-5">
          {data.opdAdvice.map((item, idx) => {
            const Icon = OPD_ICONS[idx];

            return (
              <Col md={4} key={idx}>
                <Card className="p-4 shadow-sm h-100 border-0 rounded-4">
                  <div className="icon-box-lg mb-3">
                    <Icon size={28} color="#2A8C8C" />
                  </div>

                  <h6 className="fw-semibold">{item.title}</h6>
                  <p className="text-muted small mt-2">{item.text}</p>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* ISSUE SECTION */}
        <Row className="align-items-center my- px-3">
          {/* LEFT CONTENT */}
          <Col md={6}>
            <h2 className="fw-bold mb-2">{data.issueSection.title}</h2>

            <div className="fw-semibold text-muted mb-3">
              {data.issueSection.subtitle}
            </div>

            <p className="text-muted mb-4">{data.issueSection.description}</p>

            {/* STEPS */}
            <div>
              {data.issueSection.steps.map((item, idx) => (
                <div key={idx} className="mb-3">
                  <div className="fw-bold">{item.step}</div>
                  <div className="fw-semibold">{item.title}</div>
                  <div className="text-muted small">{item.text}</div>
                </div>
              ))}
            </div>
          </Col>

          {/* RIGHT IMAGE */}
          <Col md={6} className="d-flex justify-content-center">
            <Image
              src={data.issueSection.image}
              fluid
              loading="lazy"
              className="mx-auto d-block"
              style={{
                maxHeight: 420,
                width: '100%',
                maxWidth: 450,
              }}
            />
          </Col>
        </Row>

        {/* RISK SECTION */}
        {data.riskSection && (
          <div className="mb-5 px-3">
            <Row className="align-items-center g-5">
              {/* LEFT IMAGE */}
              <Col lg={5} className="d-flex justify-content-center">
                <Image
                  src={data.riskSection.image}
                  fluid
                  rounded
                  loading="lazy"
                  className="shadow-sm mx-auto d-block"
                  style={{
                    maxWidth: 400,
                    width: '100%',
                  }}
                />
              </Col>

              {/* RIGHT CONTENT */}
              <Col lg={6}>
                {/* TITLE */}
                <h3 className="fw-bold mb-1">{data.riskSection.title}</h3>

                {/* SUBTITLE */}
                <p className="text-muted fw-semibold mb-4">
                  {data.riskSection.subtitle}
                </p>

                {/* FEATURE LIST */}
                <div className="d-flex flex-column gap-3">
                  {data.riskSection.features.map((item, idx) => {
                    const Icon = ICON_MAP[item.icon] || FaCheckCircle;

                    return (
                      <Card key={idx} className="border-0 shadow-sm rounded-4">
                        <Card.Body className="d-flex align-items-start gap-3">
                          <div className="icon-wrapper flex-shrink-0">
                            <Icon className="primary-class fs-5" />
                          </div>

                          <div>
                            <Card.Title className="fw-bold mb-1 fs-6">
                              {item.title}
                            </Card.Title>
                            <Card.Text className="text-muted small mb-0">
                              {item.description}
                            </Card.Text>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </PageWrapper>
  );
});

PageStructure2.displayName = 'PageStructure2';

export default PageStructure2;
